import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';

type Variant = 'boards' | 'favorites' | 'trash' | 'shared' | 'search' | 'explore' | 'notifications' | 'comments';

/** Простые SVG-иллюстрации для пустых состояний. */
function Illustration({ variant }: { variant: Variant }) {
  const common = 'h-28 w-40 text-fg';
  switch (variant) {
    case 'favorites':
      return (
        <svg viewBox="0 0 160 112" className={common} aria-hidden>
          <rect x="30" y="22" width="64" height="64" rx="4" fill="#FFF1A8" transform="rotate(-6 62 54)" />
          <rect x="66" y="30" width="64" height="64" rx="4" fill="#E5D4FF" transform="rotate(5 98 62)" />
          <path d="M98 40 l6 12 13 2-9.5 9 2.3 13-11.8-6.2-11.8 6.2 2.3-13-9.5-9 13-2z" fill="#FFB224" stroke="#1C1917" strokeOpacity=".15" />
        </svg>
      );
    case 'trash':
      return (
        <svg viewBox="0 0 160 112" className={common} aria-hidden>
          <rect x="52" y="34" width="56" height="62" rx="8" fill="currentColor" fillOpacity=".06" stroke="currentColor" strokeOpacity=".25" strokeWidth="2" />
          <rect x="44" y="24" width="72" height="12" rx="4" fill="currentColor" fillOpacity=".1" />
          <rect x="70" y="16" width="20" height="10" rx="3" fill="currentColor" fillOpacity=".1" />
          <path d="M68 48v36M80 48v36M92 48v36" stroke="currentColor" strokeOpacity=".2" strokeWidth="3" strokeLinecap="round" />
          <circle cx="122" cy="30" r="10" fill="#C9F2D4" />
          <path d="M117 30l3.5 3.5 6.5-7" stroke="#30A46C" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'shared':
      return (
        <svg viewBox="0 0 160 112" className={common} aria-hidden>
          <rect x="34" y="28" width="92" height="62" rx="8" fill="currentColor" fillOpacity=".05" stroke="currentColor" strokeOpacity=".2" strokeWidth="2" />
          <circle cx="62" cy="58" r="12" fill="#C7E4FF" />
          <circle cx="98" cy="58" r="12" fill="#FFC9DE" />
          <path d="M74 58h12" stroke="currentColor" strokeOpacity=".35" strokeWidth="2" strokeDasharray="3 3" />
        </svg>
      );
    case 'search':
      return (
        <svg viewBox="0 0 160 112" className={common} aria-hidden>
          <circle cx="72" cy="52" r="26" fill="currentColor" fillOpacity=".05" stroke="currentColor" strokeOpacity=".3" strokeWidth="3" />
          <path d="M91 71l20 20" stroke="currentColor" strokeOpacity=".3" strokeWidth="6" strokeLinecap="round" />
          <rect x="60" y="44" width="24" height="16" rx="2" fill="#FFF1A8" />
        </svg>
      );
    case 'notifications':
      return (
        <svg viewBox="0 0 160 112" className={common} aria-hidden>
          <path d="M80 22c-15 0-24 11-24 25v16l-8 12h64l-8-12V47c0-14-9-25-24-25z" fill="currentColor" fillOpacity=".06" stroke="currentColor" strokeOpacity=".25" strokeWidth="2" />
          <circle cx="80" cy="84" r="6" fill="currentColor" fillOpacity=".2" />
          <circle cx="104" cy="30" r="9" fill="#C9F2D4" />
        </svg>
      );
    case 'comments':
      return (
        <svg viewBox="0 0 160 112" className={common} aria-hidden>
          <path d="M40 30h64a10 10 0 0 1 10 10v28a10 10 0 0 1-10 10H66l-14 12V78H40a10 10 0 0 1-10-10V40a10 10 0 0 1 10-10z" fill="#E5D4FF" />
          <rect x="44" y="44" width="50" height="6" rx="3" fill="#1C1917" fillOpacity=".15" />
          <rect x="44" y="56" width="34" height="6" rx="3" fill="#1C1917" fillOpacity=".15" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 160 112" className={common} aria-hidden>
          <rect x="24" y="30" width="48" height="48" rx="4" fill="#FFF1A8" transform="rotate(-8 48 54)" />
          <rect x="62" y="22" width="48" height="48" rx="4" fill="#C7E4FF" transform="rotate(4 86 46)" />
          <rect x="88" y="48" width="48" height="48" rx="4" fill="#FFC9DE" transform="rotate(-3 112 72)" />
          <path d="M60 84c10 8 30 8 40-2" stroke="#6E56CF" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </svg>
      );
  }
}

export function EmptyState({ variant = 'boards', title, text, action }: { variant?: Variant; title: string; text?: ReactNode; action?: ReactNode }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="mx-auto flex max-w-sm flex-col items-center py-16 text-center"
    >
      <Illustration variant={variant} />
      <h3 className="mt-5 text-base font-semibold">{title}</h3>
      {text && <p className="mt-1.5 text-sm text-fg-muted">{text}</p>}
      {action && <div className="mt-5">{action}</div>}
    </motion.div>
  );
}
